import React from 'react';
import { Link, useLocation } from 'react-router-dom';

export const GLP1_BRIDGE_RESOURCE_LINKS = [
  {
    key: 'assistant',
    label: 'Assistant',
    title: 'Screening tool + AI',
    to: '/resources/glp1-bridge/assistant',
  },
  {
    key: 'recording',
    label: 'Recording',
    title: 'Recording + slides',
    to: '/resources/glp1-bridge/recording',
  },
  {
    key: 'guide',
    label: 'Guide',
    title: 'Clinical guide',
    to: '/resources/glp1-bridge/guide',
  },
];

function currentKeyFromPath(pathname) {
  if (pathname.endsWith('/assistant')) return 'assistant';
  if (pathname.endsWith('/recording')) return 'recording';
  if (pathname.endsWith('/guide')) return 'guide';
  return null;
}

const themes = {
  light: {
    shell: 'border-[#e5e5ea] bg-white',
    title: 'text-[#1d1d1f]',
    helper: 'text-[#6e6e73]',
    link: 'border-[#e5e5ea] bg-[#fbfbfd] text-[#424245] hover:border-[#1d1d1f] hover:bg-white hover:text-[#1d1d1f]',
    active: 'border-emerald-700 bg-emerald-50 text-[#1d1d1f]',
    label: 'text-emerald-700',
    activePill: 'bg-emerald-700 text-white',
  },
  dark: {
    shell: 'border-white/10 bg-white/[0.04]',
    title: 'text-white',
    helper: 'text-white/55',
    link: 'border-white/10 bg-white/[0.04] text-white/74 hover:border-white/30 hover:bg-white/[0.08] hover:text-white',
    active: 'border-emerald-300/70 bg-emerald-300/10 text-white',
    label: 'text-emerald-300',
    activePill: 'bg-emerald-300 text-[#111111]',
  },
};

export default function Glp1BridgeResourceNav({
  className = '',
  current,
  helperText = 'Switch GLP-1 Bridge resources.',
  layout = 'strip',
  title = 'Medicare GLP-1 Bridge resources',
  variant = 'light',
}) {
  const location = useLocation();
  const activeKey = current === 'hub' ? null : current || currentKeyFromPath(location.pathname);
  const theme = themes[variant] || themes.light;
  const isSide = layout === 'side';

  return (
    <nav
      aria-label="Medicare GLP-1 Bridge resource navigation"
      className={`rounded-lg border p-3 ${theme.shell} ${className}`}
      data-testid="glp1-bridge-resource-nav"
    >
      <div className={isSide ? 'grid gap-3' : 'grid gap-3 md:grid-cols-[minmax(180px,0.8fr)_minmax(0,2fr)] md:items-center'}>
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${theme.title}`}>{title}</p>
          <p className={`mt-0.5 text-xs leading-relaxed ${theme.helper}`}>{helperText}</p>
        </div>
        <div className={isSide ? 'grid gap-2' : 'grid gap-2 sm:grid-cols-3'}>
          {GLP1_BRIDGE_RESOURCE_LINKS.map((item) => {
            const isActive = activeKey === item.key;
            return (
              <Link
                key={item.key}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                data-resource-key={item.key}
                className={`group rounded-md border px-3 py-2 transition ${theme.link} ${isActive ? theme.active : ''}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${theme.label}`}>
                    {item.label}
                  </span>
                  {isActive ? (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${theme.activePill}`}>
                      Current
                    </span>
                  ) : null}
                </span>
                <span className="mt-1 block text-sm font-semibold leading-snug">{item.title}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
